import type { SVGProps } from 'react'
import { useCallback, useContext } from 'react'
import { Radio, RadioGroup } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import MaterialSymbolsCircleOutline from '~icons/material-symbols/circle-outline'
import MaterialSymbolsFemale from '~icons/material-symbols/female'
import MaterialSymbolsMale from '~icons/material-symbols/male'
import {
  executeSetPreferences,
  updateGetMyPreferences,
} from 'src/services/apollo/preference'
import type { Gender } from 'src/services/helpers/preference'
import { genderOptions } from 'src/services/helpers/preference'
import { track } from 'src/utils/tracker'
import { NewUserOnboardingContext } from '../NewUserOnboardingContext'
import { useNewUserOnboardingFormContext } from '../newUserOnboardingForm'
import { NewUserOnboardingFormStep } from '../NewUserOnboardingFormStep'

const genderIcons: Record<
  Gender,
  (props: SVGProps<SVGSVGElement>) => React.ReactElement
> = {
  male: MaterialSymbolsMale,
  female: MaterialSymbolsFemale,
  other: MaterialSymbolsCircleOutline,
}

function GenderSelector(props: {
  onChange: (value: string) => void
  value?: string
  disabled?: boolean
  submit: () => void
}) {
  const { t } = useTranslation(['common', 'user'])
  return (
    <RadioGroup
      className="flex gap-4 my-6"
      onChange={value => {
        props.onChange(value)
        props.submit()
      }}
      isDisabled={props.disabled}
      aria-label="gender"
    >
      {genderOptions.map(gender => {
        const Icon = genderIcons[gender]
        return (
          <Radio
            key={gender}
            className={twMerge(
              'flex-1 aspect-square rounded-xl ring-2 transition',
              'flex flex-col justify-center items-center min-[400px]:gap-2 min-[480px]:gap-4 hover:bg-theme-primary/5 focus-visible:bg-theme-primary/5',
              gender === props.value
                ? 'ring-theme-primary'
                : 'ring-neutral-200 dark:ring-neutral-600 hover:ring-theme-primary/50 hover:dark:ring-theme-primary/60 focus-visible:ring-theme-primary/50 focus-visible:dark:ring-theme-primary/60',
            )}
            value={gender}
          >
            <Icon className="size-12" />
            <p>{t(`user:onboarding.gender.option.${gender}`)}</p>
          </Radio>
        )
      })}
    </RadioGroup>
  )
}

export function NewUserGenderStep() {
  const { t } = useTranslation(['common', 'user'])
  const form = useNewUserOnboardingFormContext()
  const { gender, otherGenderName } = form.values
  const validGender = gender != null && (gender !== 'other' || otherGenderName)
  const submitGender = useCallback(async () => {
    const preferences = { gender: form.getValues().gender }
    await executeSetPreferences({ value: preferences })
    track('started_choice', {
      gender: gender,
    })
    updateGetMyPreferences(preferences)
  }, [form.getValues])

  const { actionBusy, goToNextStepWithAction } = useContext(
    NewUserOnboardingContext,
  )
  return (
    <NewUserOnboardingFormStep disableNext={!validGender} action={submitGender}>
      <h2 className="text-xl font-bold mb-4">
        {t('user:onboarding.gender.title')}
      </h2>
      <p className="text-neutral-600 dark:text-neutral-400">
        {t('user:onboarding.gender.info')}
      </p>
      <GenderSelector
        disabled={actionBusy}
        {...form.getInputProps('gender')}
        submit={() => {
          goToNextStepWithAction(submitGender)
        }}
      />
    </NewUserOnboardingFormStep>
  )
}
